import { useState } from 'react';
import PropTypes from 'prop-types';
import { useStatsigClient } from '@statsig/react-bindings';
import {
  Announcement,
  Button,
  IconCTA,
  ModalV2 as Modal,
  OverlayTriggerTooltip,
  StopIcon,
} from '@utilities';
import { GATES } from '@constants';

import { surveyStatus } from '@api/surveys';

const ForceComplete = ({
  handleForceCompleteSurvey,
  hasException,
  isHoverAction,
  isSelectedFolders,
  isSelectedItems,
  onSubmit,
  selectedRows,
  type,
}) => {
  const [error, setError] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { client } = useStatsigClient();
  const canForceCompleteSurvey = client.checkGate(GATES.COMPLETE_A_SURVEY_ACTION);

  const shouldShowForceComplete =
    (selectedRows[0].status === surveyStatus.FIELDING ||
      selectedRows[0].status === surveyStatus.FIELDED ||
      selectedRows[0].status === surveyStatus.MET_SAMPLE) &&
    selectedRows[0]?.isOwner;

  const isSurveyForceCloseAvailable =
    type === 'Survey' && canForceCompleteSurvey && shouldShowForceComplete;

  const isVisible = selectedRows.length === 1 && !hasException && isSurveyForceCloseAvailable;

  if (!isVisible) return null;

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await handleForceCompleteSurvey(selectedRows[0]);
      setIsActive(false);
      onSubmit({ refreshFolders: isSelectedFolders, refreshItems: isSelectedItems });
    } catch (error) {
      console.error(error);
      setError(error?.response?.data[0] || error?.response?.data?.detail);
      setIsLoading(false);
    }
  };

  return (
    <OverlayTriggerTooltip content={isHoverAction ? 'Force Complete' : ''}>
      <IconCTA
        data-testid="force-complete-cta"
        icon={<StopIcon />}
        onClick={() => setIsActive(true)}
        text={isHoverAction ? '' : 'Force Complete'}
      />
      <Modal
        buttons={[
          <Button
            key="cancel-btn"
            onClick={() => setIsActive(false)}
            text="Cancel"
            variant="secondary"
          />,
          <Button
            isLoading={isLoading}
            key="submit-btn"
            onClick={handleSubmit}
            text="Confirm"
            variant="primary"
          />,
        ]}
        isActive={isActive}
        onClose={() => setIsActive(false)}
        title="Stop Collecting"
      >
        {error && <Announcement text={error} variant="error" />}

        <p>This survey is actively collecting responses. </p>
        <br />
        <p>
          Closing will prevent new responses from being collected and interrupt any in-progress
          responses.
        </p>
        <br />
        <p>Once the survey is marked “Complete”, it cannot be set to “Active” again</p>
      </Modal>
    </OverlayTriggerTooltip>
  );
};

ForceComplete.propTypes = {
  handleForceCompleteSurvey: PropTypes.func,
  hasException: PropTypes.bool,
  isHoverAction: PropTypes.bool,
  isSelectedFolders: PropTypes.bool,
  isSelectedItems: PropTypes.bool,
  onSubmit: PropTypes.func,
  redirect: PropTypes.string,
  selectedRows: PropTypes.array,
  type: PropTypes.string,
};

export default ForceComplete;
